import { useEffect, useState } from "react";
import {
  useRadio,
  VisuallyHidden,
  cn,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Button,
  Checkbox,
  Input,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "@nextui-org/react";
import { EllipsisVerticalIcon, PencilSquareIcon, TrashIcon, CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { getErrorMessage } from "../../../../services/errorMessage";

/**
 * Kartu pilihan bahan B3 dengan menu aksi di sudut kanan atas.
 *
 * Komponen ini dipakai bersama oleh semua menu pelaporan (pembelian,
 * penggunaan, produksi, distribusi, pengangkutan), sehingga field yang bisa
 * diedit inline dibuat generik lewat props - bukan dihardcode ke satu field.
 *
 * PROPS GENERIK
 * - canManage          : tampilkan/sembunyikan menu titik tiga (mis. per role)
 * - showEdit           : false untuk menu yang hanya butuh aksi hapus. Item
 *                        "Edit data" tidak dirender sama sekali, dan seluruh
 *                        UI inline edit ikut tidak aktif
 * - editLabel          : label field yang diedit, mis. "No. Registrasi"
 * - editValue          : nilai field saat ini (dari baris pelaporan, dikirim induk)
 * - canEdit            : false untuk menonaktifkan menu Edit
 * - editDisabledReason : keterangan singkat kenapa Edit dinonaktifkan
 * - onUpdateValue      : async (material, nilaiBaru) => void
 * - onDelete           : async (material) => void
 * - deleteWarning      : kalimat konfirmasi hapus, disesuaikan per menu
 *
 * INFO TAMBAHAN
 * - infoTambahan : daftar {label, value} yang dicetak di bawah Nama Dagang.
 *   Dipakai menu yang perlu keterangan khusus - mis. Tipe Pembelian dan
 *   Pemasok pada pelaporan pembelian - tanpa membuat komponen ini tahu-menahu
 *   soal modul tertentu. Nilai kosong dilewati, jadi pemanggil boleh mengirim
 *   daftar tetap tanpa menyaringnya lebih dulu.
 *
 * PENANDA "TIDAK ADA DATA"
 * Dirender hanya bila onToggleNoData diisi, jadi menu yang tidak memakainya
 * tidak berubah sama sekali.
 * - noDataLabel   : tulisan di samping kotak centang, mis. "Tidak Ada Pembelian B3"
 * - noData        : status centang saat ini (dihitung induk dari baris pelaporan)
 * - isSavingNoData: kunci kotak centang selama penyimpanan berlangsung
 * - onToggleNoData: (material, dicentang) => void
 *
 * ALIAS LAMA (halaman pembelian)
 * - canEditNoRegistrasi dan onUpdateNoRegistrasi masih diterima agar halaman
 *   pembelian yang sudah terpasang tidak perlu diubah. Untuk halaman baru,
 *   gunakan nama generik di atas.
 */
export default function CardRadioButton({
  canManage = true,
  showEdit = true,
  editLabel = "No. Registrasi",
  editValue,
  canEdit,
  editDisabledReason,
  onUpdateValue,
  onDelete,
  deleteWarning,
  canEditNoRegistrasi,
  onUpdateNoRegistrasi,
  infoTambahan = [],
  noDataLabel = "Tidak ada data",
  noData = false,
  isSavingNoData = false,
  onToggleNoData,
  ...props
}) {
  const { Component, description, getBaseProps, getWrapperProps, getInputProps, getControlProps, getLabelWrapperProps } = useRadio(props);

  // `editValue` diprioritaskan. Fallback ke description.noRegistrasi supaya
  // halaman pembelian yang belum dimigrasi tetap berjalan seperti sebelumnya.
  const currentValue = (editValue ?? description?.noRegistrasi ?? "").toString();
  const isEditAllowed = showEdit && (canEdit ?? canEditNoRegistrasi ?? true);
  const updateHandler = onUpdateValue ?? onUpdateNoRegistrasi;

  const [isEditing, setIsEditing] = useState(false);
  const [draftValue, setDraftValue] = useState(currentValue);
  const [isSaving, setIsSaving] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Sinkronkan draft bila data dari server berubah (mis. setelah mutate()),
  // tapi jangan menimpa ketikan pengguna yang sedang berlangsung.
  useEffect(() => {
    if (!isEditing) setDraftValue(currentValue);
  }, [currentValue, isEditing]);

  /**
   * Kartu ini adalah <label> milik radio, jadi klik apa pun di dalamnya akan
   * ikut memilih radio. Semua area interaktif dibungkus handler ini agar
   * tombol menu / input tidak sekaligus mengubah pilihan bahan.
   */
  const stopRadioActivation = {
    onClick: (e) => {
      e.preventDefault();
      e.stopPropagation();
    },
    onMouseDown: (e) => e.stopPropagation(),
    onKeyDown: (e) => e.stopPropagation(),
  };

  const handleStartEdit = () => {
    setDraftValue(currentValue);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setDraftValue(currentValue);
    setIsEditing(false);
  };

  const handleSave = async () => {
    const next = draftValue.trim();

    // Tidak berubah -> tutup saja, jangan panggil API.
    if (next === currentValue.trim()) {
      setIsEditing(false);
      return;
    }

    if (typeof updateHandler !== "function") {
      toast.error(`Handler pembaruan ${editLabel.toLowerCase()} belum dipasang`);
      return;
    }

    setIsSaving(true);
    try {
      await updateHandler(description, next);
      setIsEditing(false);
      toast.success(`${editLabel} berhasil diperbarui`);
    } catch (error) {
      toast.error(getErrorMessage(error, `Gagal memperbarui ${editLabel.toLowerCase()}`));
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (typeof onDelete !== "function") {
      toast.error("Handler penghapusan data belum dipasang");
      return;
    }

    setIsDeleting(true);
    try {
      await onDelete(description);
      setIsConfirmOpen(false);
      toast.success("Data B3 berhasil dihapus");
    } catch (error) {
      toast.error(getErrorMessage(error, "Gagal menghapus data B3"));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Component
        {...getBaseProps()}
        className={cn(
          "relative w-full group inline-flex items-center hover:opacity-70 active:opacity-50 justify-between flex-row-reverse tap-highlight-transparent",
          "cursor-pointer border-2 border-default rounded-md gap-3 py-4 px-3",
          "data-[selected=true]:border-primary",
        )}
      >
        <VisuallyHidden>
          <input {...getInputProps()} />
        </VisuallyHidden>

        {/* Menu aksi di sudut kanan atas */}
        {canManage && (
          <div className="absolute top-1 right-1 z-20" {...stopRadioActivation}>
            <Dropdown placement="bottom-end">
              <DropdownTrigger>
                <Button isIconOnly size="sm" variant="light" radius="full" aria-label="Menu aksi data B3" className="min-w-6 w-6 h-6">
                  <EllipsisVerticalIcon className="size-4 text-default-500" />
                </Button>
              </DropdownTrigger>
              <DropdownMenu
                aria-label="Aksi data B3"
                disabledKeys={isEditAllowed || !showEdit ? [] : ["edit"]}
                onAction={(key) => (key === "edit" ? handleStartEdit() : setIsConfirmOpen(true))}
              >
                {[
                  // Item disusun sebagai array ber-key, bukan lewat
                  // `{cond && <DropdownItem/>}`. Child bernilai false bisa
                  // merusak collection builder React Aria.
                  ...(showEdit
                    ? [
                        <DropdownItem key="edit" startContent={<PencilSquareIcon className="size-4" />} description={isEditAllowed ? undefined : editDisabledReason}>
                          Edit data
                        </DropdownItem>,
                      ]
                    : []),
                  <DropdownItem key="delete" className="text-danger" color="danger" startContent={<TrashIcon className="size-4" />}>
                    Hapus data B3
                  </DropdownItem>,
                ]}
              </DropdownMenu>
            </Dropdown>
          </div>
        )}

        <span {...getWrapperProps()}>
          <span {...getControlProps()} />
        </span>

        <div {...getLabelWrapperProps()}>
          <div className="w-full flex flex-col text-xs">
            <div className="flex items-center gap-2 mb-3 pr-6">
              <span className="text-sm font-semibold">{description?.namaBahanKimia || "-"}</span>
            </div>
            <div className="w-full flex flex-col mb-3">
              <div>
                CAS Number: <span className="font-semibold">{description?.casNumber || "-"}</span>
              </div>
              <div>
                Nama Dagang: <span className="font-semibold">{description?.namaDagang || "-"}</span>
              </div>

              {infoTambahan
                .filter((info) => info?.value)
                .map((info) => (
                  <div key={info.label}>
                    {info.label}: <span className="font-semibold">{info.value}</span>
                  </div>
                ))}

              {isEditing ? (
                <div className="mt-2 flex items-end gap-1" {...stopRadioActivation}>
                  <Input
                    size="sm"
                    variant="bordered"
                    label={editLabel}
                    labelPlacement="outside"
                    placeholder={`Masukkan ${editLabel.toLowerCase()}`}
                    value={draftValue}
                    isDisabled={isSaving}
                    autoFocus
                    onValueChange={setDraftValue}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === "Enter") handleSave();
                      if (e.key === "Escape") handleCancelEdit();
                    }}
                    classNames={{ inputWrapper: "h-8 min-h-8" }}
                  />
                  <Button isIconOnly size="sm" color="primary" isLoading={isSaving} onPress={handleSave} aria-label={`Simpan ${editLabel}`}>
                    {!isSaving && <CheckIcon className="size-4" />}
                  </Button>
                  <Button isIconOnly size="sm" variant="flat" isDisabled={isSaving} onPress={handleCancelEdit} aria-label="Batal">
                    <XMarkIcon className="size-4" />
                  </Button>
                </div>
              ) : (
                currentValue && (
                  <div>
                    {editLabel}: <span className="font-semibold">{currentValue}</span>
                  </div>
                )
              )}

              {typeof onToggleNoData === "function" && (
                <div className="mt-3" {...stopRadioActivation}>
                  <Checkbox
                    size="sm"
                    isSelected={noData}
                    isDisabled={isSavingNoData}
                    onValueChange={(nilai) => onToggleNoData(description, nilai)}
                    aria-label={`${noDataLabel} untuk ${description?.namaBahanKimia || "bahan ini"}`}
                  >
                    <span className="text-xs">{noDataLabel}</span>
                  </Checkbox>
                </div>
              )}
            </div>
          </div>
        </div>
      </Component>

      {/* Konfirmasi hapus */}
      <Modal isOpen={isConfirmOpen} onOpenChange={setIsConfirmOpen} size="sm" isDismissable={!isDeleting}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="text-danger">Hapus Data B3</ModalHeader>
              <ModalBody>
                <p className="text-sm">
                  Hapus <span className="font-semibold">{description?.namaBahanKimia || "-"}</span>{" "}
                  {deleteWarning || "beserta seluruh baris pelaporannya pada periode aktif?"}
                </p>
                <p className="text-xs text-default-500">Tindakan ini tidak dapat dibatalkan.</p>
              </ModalBody>
              <ModalFooter>
                <Button variant="flat" onPress={onClose} isDisabled={isDeleting}>
                  Batal
                </Button>
                <Button color="danger" onPress={handleConfirmDelete} isLoading={isDeleting}>
                  Hapus
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}